import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { finalize } from 'rxjs';

import { PresenceModel } from '../../../models/presence.model';
import { OptionalType } from '../../../models/types/optional.type';
import { DialogService } from '../../../services/dialog.service';
import { GeneralMeetingsResource } from '../../../services/resources/general-meetings.resource';
import { DialogButtonConfigurationEnum } from '../../dialogs/general-prompt-dialog/models/enums/dialog-button-configuration.enum';
import { PresenceEditDialog } from './presence-edit/presence-edit.dialog';

@Component({
    selector: 'sl-presence',
    templateUrl: './presence.component.html',
    styleUrls: ['./presence.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PresenceComponent implements OnInit {
    readonly presence = signal<OptionalType<PresenceModel>>(undefined);
    readonly isLoading = signal(false);

    private readonly generalMeetingsResource = inject(GeneralMeetingsResource);
    private readonly dialogService = inject(DialogService);
    private readonly matDialog = inject(MatDialog);

    ngOnInit(): void {
        this.reload();
    }

    reload(): void {
        this.isLoading.set(true);
        this.generalMeetingsResource
            .getCurrentPresence()
            .pipe(finalize(() => this.isLoading.set(false)))
            .subscribe(r => this.presence.set(r.value));
    }

    showPresenceEdit(): void {
        const presence = this.presence();
        if (!presence) {
            return;
        }

        const dialogRef = this.matDialog.open(PresenceEditDialog, {
            width: '800px',
            maxWidth: '98vw',
            maxHeight: '98svh',
            data: { presencePowerPointFileData: presence.presencePowerPointFileData }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result?.updatedData) {
                // update signal so next time opening dialog shows updated data
                presence.presencePowerPointFileData = result.updatedData;
                this.presence.set({ ...presence });
            }
        });
    }

    sendToPresenter(): void {
        if (!this.presence()?.presencePowerPointFileData) {
            this.dialogService.showGeneralPromptDialog(
                {
                    promptKey: 'Presence.NoPresenterPowerPoint',
                    buttonConfiguration: DialogButtonConfigurationEnum.ConfirmCancel
                },
                () => {} // do nothing
            );
        }

        this.dialogService.showGeneralPromptDialog(
            {
                promptKey: 'Presence.ShowInPresenterPrompt',
                buttonConfiguration: DialogButtonConfigurationEnum.ConfirmCancel
            },
            yes => {
                if (yes) {
                    this.isLoading.set(true);
                    this.generalMeetingsResource
                        .showPresenceInPresenter()
                        .pipe(finalize(() => this.isLoading.set(false)))
                        .subscribe();
                }
            }
        );
    }
}
